import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import QUERIES from 'modules/common/queries';
import { IPackage, PackagesApi } from 'api';
import { useInitStateForCreateOrderByRedirectFlow } from './useStartCreateNewOrder';
import { useAuth } from 'modules/auth/hooks';
import { useCaseById } from 'modules/cases/hooks';

export const useCreateOrderRedirectFlow = () => {
  const { caseId } = qs.parse(window.location.search.substring(1));
  const queryParams = new URLSearchParams(window.location.search);
  const reference = queryParams.get('reference');
  const { user, userIsLoading } = useAuth();
  const { data: foundCase, isLoading: isCaseExistLoading } = useCaseById(caseId as string);
  const { initStateForCreateNewOrder } = useInitStateForCreateOrderByRedirectFlow();
  const [isFormSeeded, setIsFormSeeded] = useState(false);

  const doesCurrentUserHaveAccess = useMemo(
    () => !user?.isInternalUser || !!user?.branches.find(branch => branch.id === foundCase?.departmentId),
    [user, foundCase?.departmentId]
  );

  const { data: packages = [], isLoading: isPackagesLoading } = useQuery({
    queryKey: [QUERIES.packages, foundCase?.departmentId],
    queryFn: () => (foundCase?.departmentId ? PackagesApi.getPackages(foundCase.departmentId) : undefined),
    enabled: !!foundCase?.departmentId,
  });

  const typeOptions = useMemo(() => {
    const uniqueAvailablePackageTypes = [...new Set(packages.map(({ type }) => type))]; // unique values

    return [
      {
        flow: 'property',
      },
      {
        flow: 'realtor',
      },
      {
        flow: 'office',
      },
    ].filter(({ flow }) => uniqueAvailablePackageTypes.includes(flow as IPackage['type']));
  }, [packages]);

  useEffect(() => {
    if (isPackagesLoading) return;

    const firstFoundPackage = typeOptions?.[0]?.flow as 'property' | 'realtor' | 'office' | undefined;
    if (foundCase?.reference && firstFoundPackage) {
      initStateForCreateNewOrder({
        caseData: foundCase,
        packageType: firstFoundPackage as any,
        reference: foundCase.reference ?? reference,
      });
    }

    // settle even when there is nothing to seed, so the caller never spins forever
    setIsFormSeeded(true);
  }, [foundCase, typeOptions, isPackagesLoading]);

  return {
    apiState: {
      // the wizard reads the seeded form data on mount, so it must not render before seeding has run
      loading: userIsLoading || isCaseExistLoading || !isFormSeeded,
    },
    data: {
      foundCase,
      doesCurrentUserHaveAccess,
      user,
    },
  };
};
